import React, { useEffect, useState } from "react";
import { Toaster, toast } from "sonner";
import { AdminShell } from "@/components/AdminShell";
import { Plus, RefreshCw, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { Panel, Eyebrow, MonoLabel } from "@/components/Panel";
import { AdminLogin, useAdminSession } from "@/pages/admin/AdminLogin";
import { reconciliationApi } from "@/lib/reconciliationApi";
import { rupees } from "@/lib/statisticsApi";
import { apiErr } from "@/lib/authApi";

export default function Payouts() {
  const { user, setUser } = useAdminSession();
  if (!user) return <AdminLogin onSuccess={setUser} />;
  return <PayoutsConsole />;
}

function PayoutsConsole() {
  const [summary, setSummary] = useState(null);
  const [settlements, setSettlements] = useState([]);
  const [unmatched, setUnmatched] = useState({ items: [], total_paise: 0 });
  const [busy, setBusy] = useState(false);
  const [addOpen, setAddOpen] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const [s, l, u] = await Promise.all([
        reconciliationApi.summary(), reconciliationApi.list(), reconciliationApi.unreconciled(),
      ]);
      setSummary(s); setSettlements(l.items || []); setUnmatched(u);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <AdminShell>
      <Toaster theme="dark" position="top-right" />
      <div className="max-w-[1200px] mx-auto px-4 sm:px-8 py-10">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <Eyebrow className="mt-4">Admin · Payouts</Eyebrow>
            <h1 className="h-hero mt-3 text-4xl sm:text-5xl">What landed in the bank.</h1>
          </div>
          <div className="flex items-center gap-2">
            <button className="btn-ghost" onClick={load} disabled={busy} data-testid="payouts-refresh-btn"><RefreshCw size={14} /> Refresh</button>
            <button className="btn-red" onClick={() => setAddOpen(true)} data-testid="payouts-add-btn"><Plus size={14} /> Record settlement</button>
          </div>
        </div>

        {summary && (
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="payouts-summary">
            <SummaryCard k="Income booked" v={rupees(summary.income.paise)} sub={`${summary.income.count} rows`} />
            <SummaryCard k="Reconciled" v={rupees(summary.income.reconciled_paise)} sub={`${summary.income.reconciled_count} rows`} tone="green" />
            <SummaryCard k="Unreconciled" v={rupees(summary.income.unreconciled_paise)} sub={`${unmatched.items.length} payments`} tone={summary.income.unreconciled_paise > 0 ? "red" : "muted"} />
            <SummaryCard k="Net in bank" v={rupees(summary.settlements.net_paise)} sub={`${summary.settlements.count} settlements`} />
          </div>
        )}

        <Panel className="mt-6 p-0 overflow-hidden" data-testid="payouts-settlements-panel">
          <div className="p-5 border-b border-[var(--hairline)]">
            <MonoLabel>Recorded settlements · {settlements.length}</MonoLabel>
          </div>
          <table className="w-full text-sm">
            <thead className="text-left mono-label" style={{ color: "var(--taupe)" }}>
              <tr><th className="p-3">Settled on</th><th className="p-3">UTR</th><th className="p-3">Net</th><th className="p-3">Fees</th><th className="p-3">Tax</th><th className="p-3">Matched</th><th className="p-3">Unmatched</th></tr>
            </thead>
            <tbody>
              {settlements.map((s) => (
                <tr key={s.id} className="border-t border-[var(--hairline)]" data-testid={`settlement-${s.utr}`}>
                  <td className="p-3 mono-label">{s.settled_on}</td>
                  <td className="p-3 font-mono text-xs">{s.utr}</td>
                  <td className="p-3">{rupees(s.amount_paise)}</td>
                  <td className="p-3 mono-label" style={{ color: "var(--taupe)" }}>{rupees(s.fees_paise)}</td>
                  <td className="p-3 mono-label" style={{ color: "var(--taupe)" }}>{rupees(s.tax_paise)}</td>
                  <td className="p-3"><span style={{ color: "var(--green, #7ac47a)" }}>{s.matched_count}</span></td>
                  <td className="p-3"><span style={{ color: s.unmatched_count ? "var(--red-2)" : "var(--taupe)" }}>{s.unmatched_count}</span></td>
                </tr>
              ))}
              {settlements.length === 0 && (
                <tr><td colSpan={7} className="p-6 text-center mono-label" style={{ color: "var(--taupe)" }}>No settlements recorded yet.</td></tr>
              )}
            </tbody>
          </table>
        </Panel>

        {unmatched.items.length > 0 && (
          <Panel className="mt-6 p-0 overflow-hidden" data-testid="payouts-unmatched-panel">
            <div className="p-5 border-b border-[var(--hairline)] flex items-center justify-between">
              <MonoLabel><AlertTriangle size={12} className="inline-block mr-2" style={{ color: "var(--red-2)" }} />Unreconciled payments · {unmatched.items.length}</MonoLabel>
              <span className="mono-label" style={{ color: "var(--taupe)" }}>Total {rupees(unmatched.total_paise)}</span>
            </div>
            <table className="w-full text-sm">
              <thead className="text-left mono-label" style={{ color: "var(--taupe)" }}>
                <tr><th className="p-3">Date</th><th className="p-3">Payment</th><th className="p-3">Description</th><th className="p-3 text-right">Amount</th></tr>
              </thead>
              <tbody>
                {unmatched.items.slice(0, 200).map((r) => (
                  <tr key={r.id} className="border-t border-[var(--hairline)]" data-testid={`unmatched-${r.payment_ref}`}>
                    <td className="p-3 mono-label">{r.entry_date}</td>
                    <td className="p-3 font-mono text-xs">{r.payment_ref}</td>
                    <td className="p-3 text-xs" style={{ color: "var(--taupe)" }}>{r.description}</td>
                    <td className="p-3 text-right">{rupees(r.amount_paise)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Panel>
        )}

        {addOpen && <AddSettlementModal onClose={() => setAddOpen(false)} onSaved={load} unmatched={unmatched} />}
      </div>
    </AdminShell>
  );
}

function SummaryCard({ k, v, sub, tone }) {
  const color = tone === "green" ? "var(--green, #7ac47a)" : tone === "red" ? "var(--red-2)" : tone === "muted" ? "var(--taupe)" : "var(--text)";
  return (
    <Panel className="p-4">
      <div className="mono-label" style={{ color: "var(--taupe)" }}>{k}</div>
      <div className="mt-2 font-serif text-2xl" style={{ color }}>{v}</div>
      <div className="mono-label mt-1" style={{ color: "var(--taupe)" }}>{sub}</div>
    </Panel>
  );
}

function AddSettlementModal({ onClose, onSaved, unmatched }) {
  const [utr, setUtr] = useState("");
  const [razorpayId, setRazorpayId] = useState("");
  const [amount, setAmount] = useState("");
  const [fees, setFees] = useState("");
  const [tax, setTax] = useState("");
  const [settledOn, setSettledOn] = useState(new Date().toISOString().slice(0, 10));
  const [selected, setSelected] = useState(() => new Set());
  const [csvText, setCsvText] = useState("");
  const [busy, setBusy] = useState(false);
  const toggle = (id) => { const s = new Set(selected); s.has(id) ? s.delete(id) : s.add(id); setSelected(s); };

  const importCsv = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const rd = new FileReader();
    rd.onload = () => setCsvText(String(rd.result || ""));
    rd.readAsText(f);
  };

  // Auto-select from CSV pay_ids
  const csvSummary = React.useMemo(() => {
    if (!csvText) return null;
    const found = new Set();
    (csvText.match(/pay_[A-Za-z0-9_]+/g) || []).forEach((p) => found.add(p));
    // parse amounts — Razorpay settlement CSVs usually have an "amount"-ish column; be defensive:
    // extract numbers >= 100 that appear after commas as a rough total.
    const nums = (csvText.match(/(?:^|,)\s*(-?\d{1,10}(?:\.\d{1,2})?)\s*(?=,|$)/gm) || []);
    const rows = (csvText.match(/\r?\n/g) || []).length; // approx line count
    const paisaTotals = nums.map((s) => Math.round(Number(s.replace(/,/g, "")) * 100)).filter((n) => Number.isFinite(n) && n >= 100);
    const totalPaise = paisaTotals.reduce((a, b) => a + b, 0);
    const matched = [...found].filter((p) => unmatched.items.some((it) => it.payment_ref === p));
    return { rowsApprox: rows, payIds: [...found], matched, unknown: [...found].filter((p) => !matched.includes(p)), estTotalPaise: totalPaise };
  }, [csvText, unmatched.items]);

  useEffect(() => {
    if (!csvSummary) return;
    setSelected(new Set(csvSummary.matched));
    if (csvSummary.matched.length) toast.success(`Preview · ${csvSummary.matched.length} of ${csvSummary.payIds.length} pay_* IDs match`);
  }, [csvSummary]); // eslint-disable-line

  const save = async () => {
    if (!utr || !amount || !settledOn) return toast.error("UTR, amount, and settled_on are required");
    setBusy(true);
    try {
      const r = await reconciliationApi.create({
        utr, razorpay_settlement_id: razorpayId || undefined,
        amount_paise: Math.round(Number(amount) * 100),
        fees_paise: Math.round(Number(fees || 0) * 100),
        tax_paise: Math.round(Number(tax || 0) * 100),
        settled_on: settledOn, payment_ids: Array.from(selected),
      });
      toast.success(`Settlement saved · matched ${r.matched_count}, unmatched ${r.unmatched_count}`);
      onSaved(); onClose();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-6" style={{ background: "rgba(0,0,0,.7)" }}
         data-testid="settlement-modal" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <Panel className="p-6 w-full max-w-3xl mt-10">
        <div className="flex items-center justify-between">
          <MonoLabel>Record Razorpay settlement</MonoLabel>
          <button className="btn-ghost" onClick={onClose} data-testid="settlement-modal-close">Close</button>
        </div>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Field label="UTR / bank ref" v={utr} set={setUtr} testid="settlement-utr" mono />
          <Field label="Razorpay settlement ID" v={razorpayId} set={setRazorpayId} testid="settlement-rzp-id" mono />
          <Field label="Settled on (YYYY-MM-DD)" v={settledOn} set={setSettledOn} testid="settlement-date" mono />
          <Field label="Amount ₹ net" v={amount} set={setAmount} testid="settlement-amount" />
          <Field label="Fees ₹" v={fees} set={setFees} testid="settlement-fees" />
          <Field label="Tax ₹" v={tax} set={setTax} testid="settlement-tax" />
        </div>
        <div className="mt-5 hairline p-4">
          <MonoLabel>Attach payments · {selected.size} selected</MonoLabel>
          <div className="mt-2 mono-label" style={{ color: "var(--taupe)" }}>
            Upload a Razorpay settlement CSV to auto-match, or tick payments below.
          </div>
          <label className="mt-3 inline-flex btn-ghost cursor-pointer" data-testid="settlement-csv-upload">
            <input type="file" accept=".csv,.txt" className="hidden" onChange={importCsv} data-testid="settlement-csv-input" />
            Import CSV
          </label>

          {csvSummary && (
            <div className="mt-4 hairline p-3" data-testid="csv-preview" style={{ borderColor: csvSummary.matched.length ? "var(--green, #7ac47a)" : "var(--red-2)" }}>
              <MonoLabel>CSV preview</MonoLabel>
              <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                <PreviewStat k="Lines" v={csvSummary.rowsApprox} />
                <PreviewStat k="pay_* IDs found" v={csvSummary.payIds.length} />
                <PreviewStat k="Matched in DB" v={csvSummary.matched.length} tone="green" testid="csv-preview-matched" />
                <PreviewStat k="Unknown" v={csvSummary.unknown.length} tone={csvSummary.unknown.length ? "red" : "muted"} testid="csv-preview-unknown" />
                <PreviewStat k="Est. total (numbers ≥ ₹1)" v={rupees(csvSummary.estTotalPaise)} />
              </div>
              {csvSummary.unknown.length > 0 && (
                <div className="mt-3 text-xs" style={{ color: "var(--red-2)" }}>
                  Not in unreconciled worklist: <span className="font-mono">{csvSummary.unknown.slice(0, 6).join(", ")}{csvSummary.unknown.length > 6 ? ` +${csvSummary.unknown.length - 6} more` : ""}</span>
                </div>
              )}
              <div className="mt-2 mono-label" style={{ color: "var(--taupe)" }}>
                Review the auto-selected payments below · uncheck to skip · click Save when it looks right.
              </div>
            </div>
          )}
          <div className="mt-3 max-h-52 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="text-left mono-label" style={{ color: "var(--taupe)" }}>
                <tr><th className="p-2 w-6"></th><th className="p-2">Payment</th><th className="p-2">Date</th><th className="p-2 text-right">Amount</th></tr>
              </thead>
              <tbody>
                {unmatched.items.slice(0, 200).map((it) => (
                  <tr key={it.id} className="border-t border-[var(--hairline)]">
                    <td className="p-2"><input type="checkbox" checked={selected.has(it.payment_ref)} onChange={() => toggle(it.payment_ref)} data-testid={`settlement-pick-${it.payment_ref}`} /></td>
                    <td className="p-2 font-mono">{it.payment_ref}</td>
                    <td className="p-2">{it.entry_date}</td>
                    <td className="p-2 text-right">{rupees(it.amount_paise)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button className="btn-ghost" onClick={onClose} data-testid="settlement-cancel">Cancel</button>
          <button className="btn-red" onClick={save} disabled={busy} data-testid="settlement-save">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Save settlement
          </button>
        </div>
      </Panel>
    </div>
  );
}

function PreviewStat({ k, v, tone, testid }) {
  const color = tone === "green" ? "var(--green, #7ac47a)" : tone === "red" ? "var(--red-2)" : tone === "muted" ? "var(--taupe)" : "var(--text)";
  return (
    <div data-testid={testid}>
      <div className="mono-label" style={{ color: "var(--taupe)" }}>{k}</div>
      <div className="mt-1 font-mono" style={{ color }}>{v}</div>
    </div>
  );
}

function Field({ label, v, set, testid, mono }) {
  return (
    <div>
      <MonoLabel>{label}</MonoLabel>
      <input data-testid={testid} className={`input-x mt-2 ${mono ? "!font-mono" : ""}`} value={v} onChange={(e) => set(e.target.value)} />
    </div>
  );
}
